export default function (Alpine) {
    const animationTargets = [
        'btn-dark',
        'btn-primary',
        'btn-outline',
    ];

    function isDisabled(el) {
        return (
            el.disabled ||
            el.hasAttribute('disabled') ||
            el.getAttribute('aria-disabled') === 'true' ||
            el.classList.contains('disabled')
        );
    }

    function hasHref(el) {
        const href = el.getAttribute('href');

        return href && href.trim() !== '';
    }

    function isNativeAnchor(el) {
        return el.tagName.toLowerCase() === 'a';
    }

    function toMs(value) {
        value = String(value).trim();

        if (value.endsWith('ms')) return parseFloat(value);
        if (value.endsWith('s')) return parseFloat(value) * 1000;

        return parseFloat(value) || 0;
    }

    function parseTimeList(value) {
        return String(value)
            .split(',')
            .map((item) => toMs(item));
    }

    function parseCountList(value) {
        return String(value)
            .split(',')
            .map((item) => {
                item = item.trim();

                if (item === 'infinite') return Infinity;

                return parseFloat(item) || 1;
            });
    }

    function getHrefDelay(el) {
        const raw = el.getAttribute('delay');

        if (raw === null || raw.trim() === '') {
            return {
                type: 'instant',
                ms: 0,
            };
        }

        if (raw.trim().toLowerCase() === 'end') {
            return {
                type: 'end',
                ms: 0,
            };
        }

        const ms = parseInt(raw, 10);

        return {
            type: 'ms',
            ms: Number.isNaN(ms) ? 0 : Math.max(ms, 0),
        };
    }

    function getEffectiveTarget(el, event) {
        if (event?.metaKey || event?.ctrlKey || event?.button === 1) {
            return '_blank';
        }

        return el.getAttribute('target') || '_self';
    }

    function shouldPreOpenWindow(target) {
        return (
            target === '_blank' ||
            (
                target &&
                !['_self', '_top', '_parent'].includes(target)
            )
        );
    }

    function getMaxAnimationTimeFromStyle(style) {
        const names = String(style.animationName).split(',');
        const durations = parseTimeList(style.animationDuration);
        const delays = parseTimeList(style.animationDelay);
        const counts = parseCountList(style.animationIterationCount);

        let max = 0;

        names.forEach((name, index) => {
            name = name.trim();

            if (!name || name === 'none') return;

            const duration = durations[index] ?? durations[0] ?? 0;
            const delay = delays[index] ?? delays[0] ?? 0;
            const count = counts[index] ?? counts[0] ?? 1;

            if (count === Infinity) {
                max = Infinity;
                return;
            }

            max = Math.max(max, delay + duration * count);
        });

        return max;
    }

    function getMaxTransitionTimeFromStyle(style) {
        const durations = parseTimeList(style.transitionDuration);
        const delays = parseTimeList(style.transitionDelay);

        let max = 0;

        durations.forEach((duration, index) => {
            const delay = delays[index] ?? delays[0] ?? 0;

            max = Math.max(max, delay + duration);
        });

        return max;
    }

    function getMaxTime(root, type) {
        const elements = [
            root,
            ...root.querySelectorAll('*'),
        ];

        let max = 0;

        elements.forEach((el) => {
            const styles = [
                getComputedStyle(el),
                getComputedStyle(el, '::before'),
                getComputedStyle(el, '::after'),
            ];

            styles.forEach((style) => {
                if (type === 'animation') {
                    max = Math.max(max, getMaxAnimationTimeFromStyle(style));
                }

                if (type === 'transition') {
                    max = Math.max(max, getMaxTransitionTimeFromStyle(style));
                }
            });
        });

        return max;
    }

    function waitForEvent(root, events, timeout) {
        if (!timeout || timeout <= 0 || timeout === Infinity) {
            return Promise.resolve();
        }

        return new Promise((resolve) => {
            let done = false;

            const cleanup = () => {
                if (done) return;

                done = true;

                events.forEach((eventName) => {
                    root.removeEventListener(eventName, handler, true);
                });

                clearTimeout(timer);
                resolve();
            };

            const handler = (event) => {
                if (event.target !== root && !root.contains(event.target)) {
                    return;
                }

                cleanup();
            };

            events.forEach((eventName) => {
                root.addEventListener(eventName, handler, true);
            });

            const timer = setTimeout(cleanup, timeout + 80);
        });
    }

    async function waitForMotionEnd(root) {
        const waitMode = root.getAttribute('wait') || 'auto';

        await new Promise((resolve) => requestAnimationFrame(resolve));
        await new Promise((resolve) => requestAnimationFrame(resolve));

        if (root.getAnimations) {
            const animations = root.getAnimations({
                subtree: true,
            }).filter((animation) => animation.playState !== 'idle');

            if (animations.length) {
                await Promise.allSettled(
                    animations.map((animation) => animation.finished)
                );

                return;
            }
        }

        const animationTime = getMaxTime(root, 'animation');
        const transitionTime = getMaxTime(root, 'transition');

        if (waitMode === 'animation') {
            await waitForEvent(
                root,
                ['animationend', 'animationcancel'],
                animationTime
            );

            return;
        }

        if (waitMode === 'transition') {
            await waitForEvent(
                root,
                ['transitionend', 'transitioncancel'],
                transitionTime
            );

            return;
        }

        if (animationTime > 0) {
            await waitForEvent(
                root,
                ['animationend', 'animationcancel'],
                animationTime
            );

            return;
        }

        if (transitionTime > 0) {
            await waitForEvent(
                root,
                ['transitionend', 'transitioncancel'],
                transitionTime
            );
        }
    }

    function openHref(el, options = {}) {
        const href = el.getAttribute('href');

        if (!href || href.trim() === '') return;

        const target = options.target || el.getAttribute('target') || '_self';
        const openedWindow = options.openedWindow || null;

        const rel = el.getAttribute('rel') || '';
        const download = el.getAttribute('download');
        const referrerPolicy = el.getAttribute('referrerpolicy');

        if (download !== null) {
            const link = document.createElement('a');

            link.href = href;
            link.style.display = 'none';

            if (target) link.target = target;
            if (rel) link.rel = rel;
            if (download !== null) link.download = download;
            if (referrerPolicy) link.referrerPolicy = referrerPolicy;

            document.body.appendChild(link);
            link.click();
            link.remove();

            return;
        }

        if (target === '_self') {
            window.location.href = href;
            return;
        }

        if (target === '_top') {
            window.top.location.href = href;
            return;
        }

        if (target === '_parent') {
            window.parent.location.href = href;
            return;
        }

        if (openedWindow && !openedWindow.closed) {
            openedWindow.location.href = href;
            return;
        }

        window.open(
            href,
            target,
            rel.includes('noopener') ? 'noopener' : ''
        );
    }

    Alpine.data('clickAnimation', (options = {}) => ({
        animationClass: options.class || 'is-animating',
        running: false,
        runningId: 0,
        motionPromise: null,
        clickHandler: null,

        init() {
            this.clickHandler = (event) => {
                const root = this.$root;

                if (isDisabled(root)) {
                    event.preventDefault();
                    event.stopPropagation();
                    return;
                }

                const hrefExists = hasHref(root);
                const motionPromise = this.play();

                if (!hrefExists) {
                    return;
                }

                const delay = getHrefDelay(root);
                const target = getEffectiveTarget(root, event);

                if (delay.type === 'instant') {
                    if (!isNativeAnchor(root)) {
                        event.preventDefault();

                        openHref(root, {
                            target,
                        });
                    }

                    return;
                }

                event.preventDefault();

                let openedWindow = null;

                if (shouldPreOpenWindow(target)) {
                    openedWindow = window.open('', target);
                }

                if (delay.type === 'ms') {
                    setTimeout(() => {
                        openHref(root, {
                            target,
                            openedWindow,
                        });
                    }, delay.ms);

                    return;
                }

                if (delay.type === 'end') {
                    motionPromise.then(() => {
                        openHref(root, {
                            target,
                            openedWindow,
                        });
                    });
                }
            };

            this.$root.addEventListener('click', this.clickHandler);
        },

        play() {
            const root = this.$root;

            if (isDisabled(root)) {
                return Promise.resolve();
            }

            const isPerMode = root.hasAttribute('per');

            if (this.running && !isPerMode) {
                return this.motionPromise || Promise.resolve();
            }

            const currentId = ++this.runningId;

            this.running = true;

            if (isPerMode) {
                root.classList.remove(this.animationClass);
                void root.offsetWidth;
            }

            root.classList.add(this.animationClass);

            this.motionPromise = waitForMotionEnd(root).then(() => {
                if (this.runningId === currentId) {
                    root.classList.remove(this.animationClass);
                    this.running = false;
                }
            });

            return this.motionPromise;
        },

        destroy() {
            if (this.clickHandler) {
                this.$root.removeEventListener('click', this.clickHandler);
            }
        },
    }));

    function setupElement(el, index) {
        if (el.dataset.clickAnimationReady) return;

        el.dataset.clickAnimationReady = 'true';

        if (!el.style.getPropertyValue('--i')) {
            el.style.setProperty('--i', index);
        }

        const animationClass = el.dataset.animationClass || 'is-animating';

        el.setAttribute(
            'x-data',
            `clickAnimation({ class: '${animationClass}' })`
        );

        Alpine.initTree(el);
    }

    function scan() {
        const selector = animationTargets
            .map((className) => `.${CSS.escape(className)}`)
            .join(',');

        if (!selector) return;

        document.querySelectorAll(selector).forEach((el, index) => {
            setupElement(el, index);
        });
    }

    queueMicrotask(scan);

    document.addEventListener('DOMContentLoaded', () => {
        queueMicrotask(scan);
    });

    document.addEventListener('alpine:initialized', () => {
        queueMicrotask(scan);
    });
}